import OLLayer from 'ol/layer/Layer'
import OLOverlay from 'ol/Overlay'

import type { MapBrowserEvent } from 'ol'
import type { EventsKey } from 'ol/events'
import type { FeatureLike } from 'ol/Feature'
import type OLMap from 'ol/Map'

import * as PoziUtils from 'api/utils'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import GetDisplayProperties from 'data/GetDisplayProperties'
import GetPropertiesLabel from 'data/GetPropertiesLabel'
import { Label } from 'drawer/tabs/info/Label'
import { getMap, getFeatureInfoAtPixel } from 'utils/MapUtils'

let featureInfoTooltip: OLOverlay
let featureInfoTooltipElement: HTMLDivElement
let featureInfoTooltipText: string
let featureInfoFeature: FeatureLike
let featureInfoLayer: OLLayer
let featureInfoLayerConf: ConfigLayerOptions | null
let map: OLMap
let pointerMoveListener: EventsKey | null

const createFeatureInfoTooltip = () => {
  featureInfoTooltipElement = document.createElement('div')

  // The CSS can be found in measure.css
  featureInfoTooltipElement.className = 'tooltip tooltip-black hidden'

  featureInfoTooltip = new OLOverlay({
    element: featureInfoTooltipElement,
    offset: [15, 15],
    positioning: 'top-left' // position of mouse cursor relative to tooltip
  })

  if (!map) {
    map = getMap()
  }

  map.addOverlay(featureInfoTooltip)
}

const displayFeatureInfo = (evt: MapBrowserEvent<any>) => {
  // console.debug('displayFeatureInfo')

  // Do not show the tooltip if we're dragging something (e.g. when drawing)
  if (evt.dragging) {
    hideFeatureInfo()
    return
  }

  if (!map) {
    map = getMap()
  }

  // Do not try to find features when we're hovering over a map control
  const target = evt.originalEvent.target
  if (target.closest('.ol-control')) {
    return
  }

  const pixel = evt.pixel

  const featureInfos = getFeatureInfoAtPixel(pixel) || []

  // console.debug('displayFeatureInfo - featureInfos', featureInfos, pixel)

  // Use the first feature found under the pixel
  const featureInfo = featureInfos[0]

  const { olFeature, olLayer } = featureInfo || {}

  if (!olFeature || !olLayer || !(olLayer instanceof OLLayer)) {
    hideFeatureInfo()
    return
  }

  // console.debug('displayFeatureInfo - feature', pixel, olLayer.get('titleid'), olFeature, olLayer)

  // Only retrieve layerConf if the feature under the mouse is in a different layer from the previous one
  if (olLayer !== featureInfoLayer) {
    // console.debug('displayFeatureInfo - layer changed', featureInfoLayer, olLayer)

    const { layerConf, titleid } = olLayer.getProperties()
    featureInfoLayerConf = layerConf || (titleid && PoziUtils.getLayerFromTitleID(titleid))
    featureInfoLayer = olLayer
  }

  if (!featureInfoLayerConf) {
    // console.debug('displayFeatureInfo - no layerConf found!', feature, layer)

    hideFeatureInfo()
    return
  }

  // Don't show tooltips layers that have 'preventClick' set or have
  // 'queryable' set to false
  const queryable = featureInfoLayerConf.queryable ?? true
  if (featureInfoLayerConf.preventClick || !queryable) {
    hideFeatureInfo()
    return
  }

  // Only get the details for a feature if it is different from the previous one under the mouse
  if (olFeature !== featureInfoFeature) {
    const properties = olFeature.getProperties()

    featureInfoTooltipText = GetPropertiesLabel(featureInfoLayerConf, properties) || ''

    // Try label field first, if available
    if (!featureInfoTooltipText) {
      const labelField = featureInfoLayerConf.config?.spatial?.label

      if (labelField) {
        featureInfoTooltipText = PoziUtils.getPropertyValueCaseInsensitive(properties, labelField)

        // console.debug('displayFeatureInfo - labelField', feature, featureInfoLayerConf, labelField)
      }
    }

    // Use heuristics to find the best field to be used as a display field
    if (!featureInfoTooltipText) {
      const displayProperties = GetDisplayProperties(featureInfoLayerConf, properties)

      featureInfoTooltipText = Label.getDefaultValue(displayProperties)

      // console.debug('displayFeatureInfo - displayProperties', feature, featureInfoLayerConf, displayProperties, featureInfoTooltipText)
    }
    featureInfoFeature = olFeature
  }

  // Don't try to show an empty tooltip
  if (!featureInfoTooltipText) {
    console.debug('displayFeatureInfo - no featureInfoTooltipText found!', olFeature, olLayer, featureInfoTooltipText)

    hideFeatureInfo()
    return
  }

  // console.debug('displayFeatureInfo - feature', feature, layer, featureInfoTooltip, featureInfoTooltipText)

  // The tooltip element should be there already, but just in case it wasn't...
  if (!featureInfoTooltipElement) {
    // console.log('displayFeatureInfo - helpTooltipElement did not exist. Let\'s create it.')

    createFeatureInfoTooltip()
  }

  // Only set tooltip text if it's different from the one before
  if (featureInfoTooltipElement.innerHTML !== featureInfoTooltipText) {
    featureInfoTooltipElement.innerHTML = featureInfoTooltipText
  }

  // Make sure we follow the mouse cursor
  featureInfoTooltip.setPosition(evt.coordinate)

  // Just in case it was hidden, unhide it
  if (featureInfoTooltipElement.classList.contains('hidden')) {
    featureInfoTooltipElement.classList.remove('hidden')
  }

  // console.debug('displayFeatureInfo - tooltipelement', featureInfoTooltipElement)
}

const hideFeatureInfo = () => {
  if (featureInfoTooltipElement) {
    featureInfoTooltipElement.innerHTML = ''
    featureInfoTooltipElement.classList.add('hidden')
  }
}

export const disableTooltips = () => {
  // console.debug('disableTooltips')

  if (!map) {
    map = getMap()
  }

  // Stop listening to the mouse move event
  if (pointerMoveListener) {
    map.un('pointermove', displayFeatureInfo)
    pointerMoveListener = null
  }
}

export const enableTooltips = () => {
  // console.debug('enableTooltips')

  if (!map) {
    map = getMap()
  }

  // Listen to the mouse move event
  if (!pointerMoveListener) {
    pointerMoveListener = map.on('pointermove', displayFeatureInfo)
  }
}
